import { addEngravingToEcommCart, addWYWToCart } from '@/actions/cartActions';
import { getCartItemsFromCartItemGroups } from '@/components/Cart/cart.utils';
import { useAnalytics } from '@/hooks/analytics';
import { GIFT } from '@/lib/constants/memberships';
import { getItemProperties } from '@/lib/utils/analyticsHelper';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useTranslation } from '@whoop/i18n/lang/client';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { Product } from 'services/generated/commerce-service/models/Product';
import { Variant } from 'services/generated/commerce-service/models/Variant';
import {
  BAND_KEY,
  CLASP_KEY,
  FAST_LINK_KEY,
  getFirstVariant,
  HOOK_KEY,
} from 'ui';
import { useToast } from 'ui/components/Toast/use-toast';
import { getEngravingError, transformToBEModel } from 'ui/utils/engravingUtils';
import { SelectedVariantMap } from 'ui/utils/variantHelpers';
import { AmplitudeEvents } from 'whoop-analytics/lib/types';
import { useShallow } from 'zustand/react/shallow';

const AddToCartModalV2 = dynamic(
  () => import('../../accessoriesV2/AddToCartModal/AddToCartModalV2'),
  {
    ssr: false,
  },
);

const WhoopYourWayProductDetails = dynamic(
  () =>
    import(
      '../../accessoriesV2/WhoopYourWayProductDetails/WhoopYourWayProductDetails'
    ),
  {
    ssr: false,
  },
);

const Cart = dynamic(() => import('../../Cart/Cart'), {
  ssr: false,
});

const NormalProductDetails = dynamic(
  () => import('../../accessoriesV2/NormalProductDetails/NormalProductDetails'),
  {
    ssr: false,
  },
);

interface LandingPageModalsProps {
  skipMemSelection?: string;
  setSelectedVariant: (variant: Variant) => void;
  swatchProducts: Product[];
  setShowWywModal: (isOpen: boolean) => void;
  setShowEngravingModal: (isOpen: boolean) => void;
  showEngravingModal: boolean;
  showWywModal: boolean;
}

const LandingPageModals: React.FC<LandingPageModalsProps> = ({
  skipMemSelection,
  setSelectedVariant,
  swatchProducts,
  setShowWywModal,
  setShowEngravingModal,
  showEngravingModal,
  showWywModal,
}) => {
  const {
    currency,
    language,
    country,
    cart,
    setEcommCart,
    engravingInfo,
    setEngravingInfo,
    ecommCatalog,
  } = useSiteStore(
    useShallow((state) => ({
      currency: state.currency,
      language: state.language,
      country: state.country,
      cart: state.cart,
      setEcommCart: state.setEcommCart,
      engravingInfo: state.engravingInfo,
      setEngravingInfo: state.setEngravingInfo,
      ecommCatalog: state.ecommCatalog,
    })),
  );

  const { t: cartT } = useTranslation('cart', language);
  const { toast } = useToast();
  const { trackAmplitudeEvent } = useAnalytics();

  const wywParentCategory =
    ecommCatalog.accessories.find(
      (category) => category.key === 'whoop-your-way-kit',
    ) ?? null;

  const wywVariant = wywParentCategory?.products[0]
    ? getFirstVariant(wywParentCategory.products[0])
    : null;

  const [selectedWywVariants, setSelectedWywVariants] =
    useState<SelectedVariantMap>();

  const engravingProduct: Product | null =
    ecommCatalog.initial_products.find(
      (product) => product.product_type === 'engraving',
    ) ?? null;

  const disableEngraving =
    !engravingInfo?.data || Boolean(getEngravingError(engravingInfo));

  const handleWyWModalOpenChange = (isOpen: boolean) => {
    setShowWywModal(isOpen);

    // if closing modal without having wyw in cart, reset selected variant
    if (
      !isOpen &&
      getCartItemsFromCartItemGroups(cart.cartItemGroups).every(
        (item) => item.sku !== wywVariant?.sku,
      )
    ) {
      setSelectedVariant(getFirstVariant(swatchProducts[0]));
    }
  };

  const handleAddEngraving = async (
    variant: Variant,
    quantity: number,
    setIsAddingToCart: (isAddingToCart: boolean) => void,
  ) => {
    if (engravingInfo) {
      setIsAddingToCart(true);
      trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
        ...getItemProperties(engravingProduct?.name || '', variant),
        action: 'click',
      });
      try {
        const newCart = await addEngravingToEcommCart({
          requestBody: {
            cart_id: cart.id,
            country,
            locale: language,
            sku: variant.sku,
            quantity,
            engraving_params: transformToBEModel(engravingInfo),
          },
        });
        setEcommCart(newCart);
        trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
          ...getItemProperties(engravingProduct?.name || '', variant),
          action: 'success',
        });
      } catch (error) {
        toast({
          title: cartT('errorTitle'),
          description: cartT('errorDescription'),
          variant: 'error',
        });
      } finally {
        setIsAddingToCart(false);
        setShowEngravingModal(false);
      }
    }
  };

  const handleAddWYW = async (
    variant: Variant,
    quantity: number,
    setIsAddingToCart: (isAddingToCart: boolean) => void,
  ) => {
    if (selectedWywVariants) {
      setIsAddingToCart(true);
      try {
        const newCart = await addWYWToCart({
          requestBody: {
            cart_id: cart.id,
            country,
            locale: language,
            wyw_sku: variant.sku,
            quantity,
            band_sku: selectedWywVariants[BAND_KEY].sku,
            clasp_sku: selectedWywVariants[CLASP_KEY].sku,
            hook_sku: selectedWywVariants[HOOK_KEY].sku,
            fast_link_sku: selectedWywVariants[FAST_LINK_KEY].sku,
          },
        });
        setEcommCart(newCart);
        const name = wywParentCategory?.products[0]?.name || '';
        trackAmplitudeEvent(
          AmplitudeEvents.AddedItemToCart,
          getItemProperties(name, variant),
        );
      } catch (error) {
        toast({
          title: cartT('errorTitle'),
          description: cartT('errorDescription'),
          variant: 'error',
        });
      } finally {
        setIsAddingToCart(false);
        setShowWywModal(false);
      }
    }
  };

  return (
    <>
      {skipMemSelection ? (
        <Cart
          CTAButtonsVariant={
            skipMemSelection === GIFT ? 'control' : 'preferCheckout'
          }
        />
      ) : null}
      {showEngravingModal && engravingProduct ? (
        <AddToCartModalV2
          handleAddToCart={handleAddEngraving}
          isDisabled={disableEngraving}
          isOpen={showEngravingModal}
          selectedVariant={getFirstVariant(engravingProduct)}
          setIsOpen={setShowEngravingModal}
        >
          <NormalProductDetails
            currency={currency}
            engravingInfo={engravingInfo}
            group={{
              category_id: engravingProduct.product_id,
              name: engravingProduct.name,
              key: null,
              products: [engravingProduct],
              categories: [],
              description: null,
            }}
            selectedVariant={getFirstVariant(engravingProduct)}
            setEngravingInfo={setEngravingInfo}
          />
        </AddToCartModalV2>
      ) : null}
      {wywParentCategory && wywVariant && showWywModal ? (
        <AddToCartModalV2
          handleAddToCart={handleAddWYW}
          isOpen={showWywModal}
          selectedVariant={wywVariant}
          setIsOpen={handleWyWModalOpenChange}
        >
          <WhoopYourWayProductDetails
            closeModal={() => setShowWywModal(false)}
            country={country}
            language={language}
            parentCategory={wywParentCategory}
            selectedWywOptions={selectedWywVariants}
            setSelectedWywOptions={setSelectedWywVariants}
          />
        </AddToCartModalV2>
      ) : null}
    </>
  );
};

export default LandingPageModals;
